import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../config/config.service';
import { LocalQueueService } from './local-queue.service';
import { QueuedOp, QueuedOpKind } from './queue.types';

@Injectable({ providedIn: 'root' })
export class SyncService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private readonly queue = inject(LocalQueueService);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly _online = signal(typeof navigator === 'undefined' ? true : navigator.onLine);
  private readonly _pending = signal(0);
  private draining = false;

  readonly online = this._online.asReadonly();
  readonly pendingCount = this._pending.asReadonly();

  constructor() {
    if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this._online.set(true);
        void this.drain();
      });
      window.addEventListener('offline', () => this._online.set(false));
    }
    void this.refresh();
  }

  async refresh(): Promise<void> {
    this._pending.set(await this.queue.pendingCount());
  }

  async enqueue(kind: QueuedOpKind, payload: unknown, clientUuid: string): Promise<void> {
    await this.queue.enqueue(kind, payload, clientUuid);
    await this.refresh();
  }

  async drain(): Promise<void> {
    if (this.draining || !this._online()) {
      return;
    }
    this.draining = true;
    try {
      const ops = await this.queue.pending();
      for (const op of ops) {
        try {
          await this.send(op);
          await this.queue.markSent(op.id);
        } catch (e) {
          await this.queue.markFailed(op.id, String(e));
        }
      }
      await this.refresh();
    } finally {
      this.draining = false;
    }
  }

  private async send(op: QueuedOp): Promise<void> {
    const url = this.endpoint(op.kind);
    await firstValueFrom(this.http.post(url, op.payload));
  }

  private endpoint(kind: QueuedOpKind): string {
    const api = this.config.apiUrl();
    switch (kind) {
      case 'sale':
        return `${api}/pos/commit-sale`;
      case 'fiscal_report':
        return `${api}/pos/fiscal-reports`;
    }
  }
}
